<template>
  <ValidationObserver v-slot="{ handleSubmit}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" :title="$t(`Reassign Reservations`)" @hidden="onHidden"
                   @show="onShow"
                   @ok.prevent="handleSubmit(onSubmit)" :disable-submit-btn="items.length===0" :ok-title="$t('Submit')"
                   :is-loading="loading" :body-class="'pt-0'" scrollable>
      <div class="modal-height">
        <spinner :show="modalLoading" :height="33"/>
        <div class="pt-2 pb-2 border-bottom d-flex align-items-center" v-if="!modalLoading">

          <sale-select-input-widget class="sale-select-widget" :model="defaultReservation" attribute="sale_no"
                                    value-field="sale_no" :label="false" @select="onDefaultSaleSelect" ref="defaultSale"
                                    :disabled="selectedItems.size === 0" :items="saleOptions"
                                    :options="{placeholder: $t('Reassign Lots to Sale')}"/>
          <b-button variant="primary" class="ml-auto" @click="onClearLotsLick">
            {{ $t("Clear Lots") }}
          </b-button>

          <div class="default-sale-input">
          </div>
        </div>
        <table class="info-table table table-sm lots-table mb-0" v-if="!modalLoading">
          <tr>
            <th>
              <b-form-checkbox @change="selectAllRows($event)" v-model="isSelectedAll"/>
            </th>
            <th style="width: 12%">{{ $t('Stock No') }}</th>
            <th style="width: 25%">{{ $t('Title') }}</th>
            <th style="width: 10%">{{ $t('Quantity') }}</th>
            <th style="width: 12%">{{ $t('Status') }}</th>
            <th style="width: 16%">
              {{ $t('Lot') }}
              <span>
                <b-tooltip :target="`lot-question-mark`"
                           :title="$t('If left blank, following item will be reassigned with reserved status.')"/>
                <font-awesome-icon id="lot-question-mark" icon="question-circle" class="icon ml-2"/>
              </span>
            </th>
            <th style="width: 20%">{{ $t('Sale') }}</th>
            <th style="width: 5%">{{ "" }}</th>
          </tr>
          <tr :key="`items-to-reassign-${index}`" v-for="(item, index) of items">
            <td>
              <b-form-checkbox style="padding-left: 2.01em" @change="onRowSelect($event, item)"
                               :checked="selectedItems.has(item)"/>
            </td>
            <td>
              <router-link v-if="$hasPermission('view_receipt')"
                           :to="{name: 'receipt.view', params: {id: item.inventory.receipt_uuid}}">
                {{ item.inventory.receipt.receipt_no }}
              </router-link>
              /
              <router-link v-if="$hasPermission('view_inventoryitem')"
                           :to="{ name: 'inventory.view', params: { id: item.inventory_uuid }}">
                {{ item.inventory.item_no }}
              </router-link>
            </td>
            <td>
              {{ item.inventory.short_description | striptags | truncate(100) }}
            </td>
            <td>
              {{ item.quantity }}
            </td>
            <td>
              <span v-as-status="item.status">{{ getStatus(item) | removeUnderscores | toUpperCaseFirst }}</span>
            </td>
            <td>
              <input-widget :model="item" attribute="lot_no" :label="false" :rules="{regex: LOT_NO_REGEX}"/>
            </td>
            <td>
              <sale-select-input-widget :model="item" attribute="auction_uuid" :label="false" rules="required"
                                        :key="`sale-widget-${index}-${item.index}`"/>
            </td>
            <td>
              <div v-if="items.length!==1" style="min-height: 2.5em"
                   class="d-flex align-items-center justify-content-center">
                <b-tooltip :target="`remove-reservation-button-${index}`" :title="$t('Delete')"/>
                <font-awesome-icon class="text-danger hover-cursor" :id="`remove-reservation-button-${index}`"
                                   :icon="'times'" style="font-size: 1.3em;" @click="onRemoveItem(index)"
                />
              </div>
            </td>
          </tr>
        </table>
      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "@/core/components/artisio-modal/ArtisioModal.vue";
  import {createNamespacedHelpers} from "vuex";
  import SaleSelectInputWidget from "@/core/widgets/SaleSelectInputWidget.vue";
  import InputWidget from "@/core/components/input-widget/InputWidget.vue";
  import LotsService from "@/modules/lots/LotsService";
  import ReservationModel from "@/modules/inventory/Reservation.model";
  import {WS_BATCH_REASSIGN_PROGRESS} from "@/modules/constants";
  import Spinner from "@/core/components/common/Spinner.vue";
  import {LOT_NO_REGEX} from '@/plugins/vee-validate';
  import cloneDeep from "lodash/cloneDeep";
  import SalesService from "@/modules/sales/SalesService";
  import helperService from "@/core/services/helper.service";

  const {mapState, mapActions} = createNamespacedHelpers('lots');
  const {mapActions: globalMapActions} = createNamespacedHelpers('global');

  export default {
    name: "ReassignModal",
    components: {InputWidget, SaleSelectInputWidget, ArtisioModal, Spinner},
    computed: {
      ...mapState({
        modal: state => state.reassignModal,
        modalLoading: state => state.reassignModal.loading,
      }),
    },
    watch: {
      'modal.reservations'() {
        this.items = Array.from(this.modal.reservations).map(item => {
          return new ReservationModel({
            ...item,
            auction_uuid: null,
            auction: null,
            index: 1
          });
        })
        this.mappedItems = this.items.reduce(function (obj, item) {
          obj[item.uuid] = item;
          return obj;
        }, {});
      }
    },
    data() {
      return {
        items: [],
        mappedItems: [],
        progressUuid: null,
        syncProgressValue: 0,
        loading: false,
        LOT_NO_REGEX: LOT_NO_REGEX,
        isSelectedAll: false,
        selectedItems: new Set(),
        defaultReservation: new ReservationModel(),
        saleOptions: []
      }
    },
    methods: {
      ...mapActions(['hideReassignModal', 'getTableData']),
      ...globalMapActions(['addProgress', 'updateProgressValue', 'removeProgress']),
      onHidden() {
        this.$ws.remove(`lots.${WS_BATCH_REASSIGN_PROGRESS}`);
        this.clearSelected()
        this.hideReassignModal();
      },
      getStatus(item) {
        if (item.status.includes('lotted') && this.enableContinuousAuction) {
          if (item.auction && item.auction.status.includes('published')) {
            return item.auction.status;
          }
        }
        return item.status;
      },
      async onSubmit() {
        const reservation_proposals = (await LotsService.getLatestReservationProposals({
          reservation_uuids: this.items.map(item => item.uuid),
          suggestions: ['return', 'transfer'],
        })).body;
        if (reservation_proposals.length) {
          const confirm = await helperService.showProposalsConfirmationModal(reservation_proposals, this.$confirm, this.$router, this.$options.filters)
          if (!confirm) return;
        }

        let data = Array.from(this.items).map(item => {
          return {
            lot_no: item.lot_no,
            auction_uuid: item.auction_uuid,
            uuid: item.uuid
          }
        })
        this.loading = true;
        LotsService.reassignReservations(data);
      },
      onRemoveItem(index) {
        this.items.splice(index, 1);
        this.items = [...this.items];
      },
      onShow() {
        this.$ws.listen(`lots.${WS_BATCH_REASSIGN_PROGRESS}`, this.onBatchReassignDataReceive);
      },
      async onBatchReassignDataReceive(data) {
        this.syncProgressValue = parseInt(Math.round((data.successful / data.total) * 100))

        if (data.is_finished) {
          this.loading = false;
          this.syncProgressValue = null;
          this.removeProgress(this.progressUuid);
          this.progressUuid = null;

          if (!data.errors) {
            this.$toast('success', this.$t('Reservations were successfully reassigned'));
            this.hideReassignModal();
            this.getTableData();
          } else {
            this.items.forEach(res => res.resetErrors())
            if (data.errors.non_field_errors) {
              this.$errorDialog(data.errors.non_field_errors[0], this.$i18n.t('Reassign errors'));
            } else {
              this.handleErrors(data.errors);
            }
          }
        } else {
          let progress = null;
          const name = this.$i18n.t('Reassigning Reservations');

          if (!this.progressUuid) {
            progress = await this.addProgress(name);
            this.progressUuid = progress.uuid;
          }

          let obj = {
            name,
            progressValue: this.syncProgressValue,
            uuid: this.progressUuid
          }
          this.updateProgressValue(obj);
        }
      },
      handleErrors(mappedErrors) {
        for (let [uuid, errors] of Object.entries(mappedErrors)) {
          let itemModel = this.mappedItems[uuid];

          for (let [attr, err] of Object.entries(errors)) {
            itemModel.setErrors(attr, [err]);
          }
        }
      },
      selectAllRows(checked) {
        this.clearSelected();
        this.isSelectedAll = checked;
        if (this.isSelectedAll) {
          this.items.forEach(item => this.selectedItems.add(item));
        } else {
          this.items.forEach(item => this.clearItem(item));
        }
        this.selectedItems = new Set(this.selectedItems);
      },
      clearItem(item) {
        item.auction_uuid = null
        item.auction = null
        item.index += 1
      },
      onRowSelect(checked, item) {
        if (checked) {
          this.selectedItems.add(item)
        } else {
          this.selectedItems.delete(item)
          this.clearItem(item)
        }
        this.isSelectedAll = this.selectedItems.size === this.items.length;
        this.selectedItems = new Set(this.selectedItems);
      },
      clearSelected() {
        this.selectedItems.clear();
        this.selectedItems = new Set([...this.selectedItems]);
        this.isSelectedAll = false;
      },
      async onDefaultSaleSelect(auction) {
        this.selectedItems.forEach(item => {
          item.auction_uuid = auction.uuid;
          item.auction = cloneDeep(auction);
        });
        this.defaultReservation = new ReservationModel();
      },
      onClearLotsLick() {
        this.items.forEach(item => item.lot_no = null)
      }
    },
  }
</script>

<style lang="scss" scoped>
  @import "../scss/variables";

  .sale-select-widget {
    width: 15em;

    /deep/ .form-group {
      margin-bottom: 0;
    }
  }

  td {
    padding-top: 1em;
    height: 7em;
  }

  th {
    border-top: 0;
    padding: 0.7em;
  }

  .modal-height {
    height: 50em;
  }

</style>
